import Select from 'react-select';
import {
    DEFAULT_ID_TYPE,
    mapPaisesToSelectOptions,
    mapMunicipiosToSelectOptions,
    getDefaultColombiaCountryValue,
} from '../../utils/locationFormHelpers';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Swal from 'sweetalert2';
import { alertaCargandoProceso, alertaGeneral, alertaToastTopEnd } from '../../utils/alertas';
import { validarInputExpresionRegular } from '../../utils/generalFunctions';
import apiClient from '../../utils/axiosPetition';
import InputFile from '../../utils/InputFile';
import { useDataUser } from '../../context/dataUserContext';
import ImageCropper from '../ImageCropper/ImageCropper';

const PATIENT_DRAFT_DB_NAME = 'PatientPerfilDrafts';
const PATIENT_DRAFT_DB_STORE = 'files';
const PATIENT_FILE_KEYS = ['foto_perfil', 'cedulaPDF'];

function openPatientDraftDB() {
    return new Promise((resolve, reject) => {
        const r = indexedDB.open(PATIENT_DRAFT_DB_NAME, 1);
        r.onerror = () => reject(r.error);
        r.onsuccess = () => resolve(r.result);
        r.onupgradeneeded = (e) => { e.target.result.createObjectStore(PATIENT_DRAFT_DB_STORE); };
    });
}

function savePatientDraftFile(draftKey, fieldName, file) {
    if (!file || !(file instanceof File)) return Promise.resolve();
    return openPatientDraftDB().then(db => new Promise((resolve, reject) => {
        const tx = db.transaction(PATIENT_DRAFT_DB_STORE, 'readwrite');
        tx.objectStore(PATIENT_DRAFT_DB_STORE).put({ name: file.name, type: file.type, blob: file }, `${draftKey}_${fieldName}`);
        tx.oncomplete = () => { db.close(); resolve(); };
        tx.onerror = () => reject(tx.error);
    }));
}

function getPatientDraftFile(draftKey, fieldName) {
    return openPatientDraftDB().then(db => new Promise((resolve, reject) => {
        const tx = db.transaction(PATIENT_DRAFT_DB_STORE, 'readonly');
        const req = tx.objectStore(PATIENT_DRAFT_DB_STORE).get(`${draftKey}_${fieldName}`);
        req.onsuccess = () => {
            db.close();
            const row = req.result;
            if (!row || !row.blob) return resolve(null);
            resolve(new File([row.blob], row.name || 'file', { type: row.type || row.blob.type }));
        };
        req.onerror = () => reject(req.error);
    }));
}

function clearPatientDraftFiles(draftKey) {
    if (!draftKey) return Promise.resolve();
    return openPatientDraftDB().then(db => new Promise((resolve, reject) => {
        const tx = db.transaction(PATIENT_DRAFT_DB_STORE, 'readwrite');
        const store = tx.objectStore(PATIENT_DRAFT_DB_STORE);
        tx.oncomplete = () => { db.close(); resolve(); };
        tx.onerror = () => reject(tx.error);
        const range = IDBKeyRange.bound(draftKey, draftKey + '\uffff');
        const req = store.getAllKeys(range);
        req.onsuccess = () => {
            (req.result || []).forEach(k => store.delete(k));
        };
        req.onerror = () => reject(req.error);
    }));
}

const PATIENT_DRAFT_META_KEYS = ['_patientId', '_hasFile'];
const PATIENT_DRAFT_PRELOADED_KEYS = ['firstName', 'secondName', 'firstLastName', 'secondLastName', 'email', 'cellNumber', 'idType', 'idNumber', 'foto_perfil', 'cedulaPDF'];

function isPatientDraftMeaningful(parsed) {
    if (!parsed || typeof parsed !== 'object') return false;
    const hasFiles = parsed._hasFile && Object.keys(parsed._hasFile).length > 0;
    if (hasFiles) return true;
    for (const key of Object.keys(parsed)) {
        if (PATIENT_DRAFT_META_KEYS.includes(key) || PATIENT_DRAFT_PRELOADED_KEYS.includes(key)) continue;
        const v = parsed[key];
        if (v === null || v === undefined) continue;
        if (typeof v === 'string' && v.trim() === '') continue;
        if (typeof v === 'number' && !Number.isNaN(v)) return true;
        if (typeof v === 'string' && v.trim() !== '') return true;
        if (typeof v === 'object' && v !== null && (v.value !== undefined || v.label !== undefined)) return true;
    }
    return false;
}

/** Comprime la foto de perfil antes de subirla (reduce errores de tamaño en nginx/servidor). */
const compressProfileImage = (file, maxWidth = 800, quality = 0.72) => new Promise((resolve) => {
    if (!file || !file.type?.startsWith('image/')) {
        resolve(file);
        return;
    }
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);
    img.onload = () => {
        let { width, height } = img;
        if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
        }
        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(img, 0, 0, width, height);
        canvas.toBlob((blob) => {
            URL.revokeObjectURL(objectUrl);
            if (!blob) {
                resolve(file);
                return;
            }
            resolve(new File([blob], (file.name || 'foto_perfil.jpg').replace(/\.\w+$/, '.jpg'), {
                type: 'image/jpeg',
                lastModified: Date.now(),
            }));
        }, 'image/jpeg', quality);
    };
    img.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        resolve(file);
    };
    img.src = objectUrl;
});

const isExistingPhotoUrl = (value) => typeof value === 'string' && /^https?:\/\//i.test(value);

const CompleteProfilePatient = ({ patient }) => {
    const { role, doctorId, patientId } = useDataUser();
    const draftStorageKey = (patientId || patient?._id) ? `patient_perfil_draft_${patientId || patient?._id}` : null;
    const [departments, setDepartments] = useState([]);
    const [cities, setCities] = useState([]);
    const [loading, setLoading] = useState(false);
    const [doctor, setDoctor] = useState(null);
    const [isDoctor, setIsDoctor] = useState(false);
    const [showForm, setShowForm] = useState(false);

    const [selectedAseguradora, setSelectedAseguradora] = useState(null);
    const [selectedOcupacion, setSelectedOcupacion] = useState(null);

    const [aseguradoraOptions, setAseguradoraOptions] = useState([]);
    const [ocupacionOptions, setOcupacionOptions] = useState([]);
    const [paises, setPaises] = useState([]);
    
    // Estados para el recortador de imagen (ref evita perder el archivo si hay re-render en producción)
    const [showImageCropper, setShowImageCropper] = useState(false);
    const [imageToCrop, setImageToCrop] = useState(null);
    const fileToCropRef = useRef(null);
    const [photoPreviewUrl, setPhotoPreviewUrl] = useState(null);
    const [photoPreviewError, setPhotoPreviewError] = useState(false);

    const [formData, setFormData] = useState({
        foto_perfil: patient?.urlPictureProfile || '',
        dob: '',
        birthCountry: '',
        residenceCountry: '',
        municipio: '',
        city: '',
        neighborhood: '',
        firstName: patient?.firstName || '',
        secondName: patient?.secondName || '',
        firstLastName: patient?.firstLastName || '',
        secondLastName: patient?.secondLastName || '',
        estadoCivil: '',
        zonaUbicacion: '',
        grupoSanguineo: '',
        rh: '',
        address: '',
        grupoEtnico: '',
        gender: '',
        sex: '',
        condicionDesplazamiento: '',
        escolaridad: '',
        cellNumber: patient?.cellNumber || '',
        cellNumber1: '',
        email: patient?.email || '',
        idType: patient?.idType?.toUpperCase() || DEFAULT_ID_TYPE,
        idNumber: patient?.idNumber || '',
        tipoUsuario: '',
        cedulaPDF: patient?.urlFileDocument || '',
        hasMedicalInsurance: 'no',
        insuranceName: '',
        aseguradora: '',
        insuranceNumber: '',
        hasCarer: 'no',
        ocupacion: '',
        carerFirstName: '',
        carerSecondName: '',
        carerFirstLastName: '',
        carerSecondLastName: '',
        carerRelationship: '',
        carerIdType: '',
        carerIdNumber: '',
    });

    // Vista previa circular de la foto (archivo recién recortado o URL existente)
    useEffect(() => {
        if (formData.foto_perfil instanceof File) {
            const url = URL.createObjectURL(formData.foto_perfil);
            setPhotoPreviewError(false);
            setPhotoPreviewUrl(url);
            return () => URL.revokeObjectURL(url);
        }
        const existingUrl = isExistingPhotoUrl(formData.foto_perfil)
            ? formData.foto_perfil
            : (patient?.urlPictureProfile || '');
        setPhotoPreviewError(false);
        setPhotoPreviewUrl(existingUrl || null);
    }, [formData.foto_perfil, patient?.urlPictureProfile]);

    useEffect(() => {
        const fetchDepartments = async () => {
            try {
                const response = await apiClient.get(`/api/clinic/departments`);
                setDepartments(response.data);
            } catch (error) {
                console.error('Error fetching departments:', error);
            }
        };

        fetchDepartments();

    }, []);

    const fetchPaises = async () => {
        try {
            const response = await apiClient.get(`/api/clinic/getPaises`);
            setPaises(response.data);
        } catch (error) {
            console.error('Error fetching países:', error);
        }
    };

    // Llama a fetchPaises cuando el componente se monte
    useEffect(() => {
        fetchPaises();
    }, []);

    useEffect(() => {
        if (!paises.length) return;
        const defaultCountry = getDefaultColombiaCountryValue(paises);
        if (!defaultCountry) return;

        setFormData((prev) => {
            const next = { ...prev };
            let changed = false;

            if (!prev.birthCountry && !patient?.birthCountry) {
                next.birthCountry = defaultCountry;
                changed = true;
            }
            if (!prev.residenceCountry && !patient?.residenceCountry) {
                next.residenceCountry = defaultCountry;
                changed = true;
            }
            if (!prev.idType && !patient?.idType) {
                next.idType = DEFAULT_ID_TYPE;
                changed = true;
            }

            return changed ? next : prev;
        });
    }, [paises, patient?.birthCountry, patient?.residenceCountry, patient?.idType]);

    // Verificar si el usuario es doctor y obtener tus datos
    useEffect(() => {
        const checkIfDoctor = async () => {
            if (role === 'doctor' && doctorId) {
                setIsDoctor(true);
                try {
                    const response = await apiClient.get(`/api/doctor/${doctorId}`);
                    const doctorData = response.data;
                    setDoctor(doctorData);
                    
                    // Pre-llenar email, cellNumber, idType e idNumber del doctor automáticamente
                    if (doctorData) {
                        setFormData(prevFormData => ({
                            ...prevFormData,
                            // Priorizar datos del doctor si están disponibles
                            // Solo usar datos del paciente si el doctor no tiene esos datos
                            email: doctorData.email || prevFormData.email || '',
                            cellNumber: doctorData.cellNumber || prevFormData.cellNumber || '',
                            idType: doctorData.idType ? doctorData.idType.toUpperCase() : (prevFormData.idType || ''),
                            idNumber: doctorData.idNumber || prevFormData.idNumber || '',
                        }));
                    }
                } catch (error) {
                    console.error('Error obteniendo datos del doctor:', error);
                }
            }
        };
        checkIfDoctor();
    }, [role, doctorId]);

    const fetchAseguradoraOptions = async (query) => {
        setLoading(true);

        try {
            const response = await apiClient.get(
                `/api/clinic/searchAseguradora`,
                {
                    params: { query }
                }
            );

            const options = response.data.map((med) => ({
                value: med.Os,         // Código CIE-10
                label: med.Nombre         // Nombre de la enfermedad

            }));

            setAseguradoraOptions(options);
        } catch (error) {
            console.error('Error al obtener las Aseguradoras:', error);
        } finally {
            setLoading(false);
        }
    };
    const fetchOcupacionOptions = async (query) => {
        setLoading(true);

        try {
            const response = await apiClient.get(
                `/api/clinic/searchOcupacion`,
                {
                    params: { query },
                }
            );

            const options = response.data.map((med) => ({
                value: med.Indice,
                label: med.Detalle

            }));

            setOcupacionOptions(options);
        } catch (error) {
            console.error('Error al obtener las Ocupaciones:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (formData.department) {
            // Fetch municipalities when a department is selected
            const fetchCities = async () => {
                if (formData.department) {
                    try {
                        const response = await apiClient.get(`/api/clinic/cities/${formData.department}`);
                        setCities(response.data);
                    } catch (error) {
                        console.error('Error fetching cities:', error);
                    }
                }
            };

            fetchCities();
        } else {
            setCities([]);
        }
    }, [formData.department]);

    // Restaurar borrador al montar (si existe y tiene contenido)
    useEffect(() => {
        if (!draftStorageKey) return;
        try {
            const raw = localStorage.getItem(draftStorageKey);
            if (!raw) return;
            const parsed = JSON.parse(raw);
            const currentId = patientId || patient?._id;
            if (parsed._patientId !== currentId) return;
            if (!isPatientDraftMeaningful(parsed)) {
                localStorage.removeItem(draftStorageKey);
                clearPatientDraftFiles(draftStorageKey);
                return;
            }
            const hasFiles = parsed._hasFile && Object.keys(parsed._hasFile).length > 0;
            Swal.fire({
                title: 'Borrador encontrado',
                html: hasFiles
                    ? 'Hay datos y archivos guardados de una sesión anterior. ¿Deseas restaurarlos?'
                    : 'Hay datos guardados de una sesión anterior. ¿Deseas restaurarlos?',
                icon: 'question',
                showCancelButton: true,
                confirmButtonText: 'Sí, restaurar',
                cancelButtonText: 'No, empezar de nuevo',
                customClass: { title: 'swal2-title-custom', htmlContainer: 'swal2-html-container-custom' }
            }).then((result) => {
                if (result.isConfirmed) {
                    const restored = { ...parsed };
                    delete restored._patientId;
                    delete restored._hasFile;
                    PATIENT_FILE_KEYS.forEach(k => { restored[k] = ''; });
                    setFormData(prev => ({ ...prev, ...restored }));
                    if (restored.aseguradora) setSelectedAseguradora({ value: restored.aseguradora, label: restored.aseguradora });
                    if (restored.ocupacion) setSelectedOcupacion({ value: restored.ocupacion, label: restored.ocupacion });
                    setShowForm(true);
                    const fileKeysToRestore = parsed._hasFile ? Object.keys(parsed._hasFile) : [];
                    if (fileKeysToRestore.length > 0) {
                        Promise.all(fileKeysToRestore.map(k => getPatientDraftFile(draftStorageKey, k)))
                            .then(files => {
                                const byKey = {};
                                fileKeysToRestore.forEach((k, i) => { if (files[i]) byKey[k] = files[i]; });
                                if (Object.keys(byKey).length > 0) setFormData(prev => ({ ...prev, ...byKey }));
                            })
                            .catch(() => {});
                    }
                } else {
                    localStorage.removeItem(draftStorageKey);
                    clearPatientDraftFiles(draftStorageKey);
                }
            });
        } catch (e) {
            localStorage.removeItem(draftStorageKey);
            clearPatientDraftFiles(draftStorageKey);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [draftStorageKey]);

    // Guardar borrador en localStorage + archivos en IndexedDB (debounced)
    const saveDraftRef = useRef(null);
    useEffect(() => {
        if (!draftStorageKey || !showForm) return;
        if (saveDraftRef.current) clearTimeout(saveDraftRef.current);
        saveDraftRef.current = setTimeout(async () => {
            try {
                const currentId = patientId || patient?._id;
                const draft = { _patientId: currentId, _hasFile: {} };
                const filePromises = [];
                for (const key in formData) {
                    const value = formData[key];
                    if (value instanceof File) {
                        draft[key] = null;
                        draft._hasFile[key] = true;
                        filePromises.push(savePatientDraftFile(draftStorageKey, key, value));
                    } else {
                        draft[key] = value;
                    }
                }
                await Promise.all(filePromises);
                localStorage.setItem(draftStorageKey, JSON.stringify(draft));
            } catch (e) {
                console.warn('No se pudo guardar el borrador del perfil paciente:', e);
            }
            saveDraftRef.current = null;
        }, 800);
        return () => { if (saveDraftRef.current) clearTimeout(saveDraftRef.current); };
    }, [formData, draftStorageKey, showForm, patientId, patient]);

    const handleChange = (e) => {
        // Leer del evento de forma síncrona (en producción el evento puede ser reutilizado)
        const target = e?.target;
        if (!target) return;
        const name = target.name;
        const type = target.type;
        const files = target.files;
        const file = files?.[0];

        // Si es la foto de perfil, abrir el recortador (mostrar overlay siempre para detectar fallos en producción)
        if (name === 'foto_perfil' && type === 'file') {
            target.value = '';
            if (file) {
                fileToCropRef.current = file;
                setImageToCrop(file);
            } else {
                fileToCropRef.current = null;
                setImageToCrop(null);
            }
            setShowImageCropper(true);
            return;
        }

        setFormData(prevState => ({
            ...prevState,
            [name]: type === 'file' ? file : validarTipoExpresionRegular(e),
        }));
    };
    
    const handleCropComplete = (croppedFile) => {
        setPhotoPreviewError(false);
        setFormData(prevState => ({
            ...prevState,
            foto_perfil: croppedFile,
        }));
        fileToCropRef.current = null;
        setShowImageCropper(false);
        setImageToCrop(null);
    };

    const handleCropCancel = () => {
        fileToCropRef.current = null;
        setShowImageCropper(false);
        setImageToCrop(null);
    };

    const handleAseguradoraChange = (selectedOption) => {
        setSelectedAseguradora(selectedOption); // Mantiene el valor seleccionado en su estado individual
        setFormData({
            ...formData,
            aseguradora: selectedOption ? selectedOption.label : '', // Actualiza insuranceName en formData
        });
    };
    const handleOcupacionChange = (selectedOption) => {
        setSelectedOcupacion(selectedOption); // Mantiene el valor seleccionado en su estado individual
        setFormData({
            ...formData,
            ocupacion: selectedOption ? selectedOption.label : '', // Actualiza insuranceName en formData
        });
    };

    const handleSelectChange = (e) => {
        const { name, value } = e.target;
        setFormData(prevState => ({
            ...prevState,
            [name]: value,
        }));
    };

    const [municipios, setMunicipios] = useState([]);

    const fetchMunicipios = async () => {
        try {
            const response = await apiClient.get(`/api/clinic/getMunicipios`);
            setMunicipios(response.data);
        } catch (error) {
            console.error('Error fetching municipios:', error);
        }
    };


    useEffect(() => {
        fetchMunicipios();
    }, []);

    const handleMunicipioChange = selectedOption => {
        setFormData({ ...formData, municipio: selectedOption.value });
    };

    const opcionesPaises = mapPaisesToSelectOptions(paises);
    const opcionesMunicipios = mapMunicipiosToSelectOptions(municipios);


    /* Funcion guardar formulario */
    const handleSubmit = async (e) => {
        e.preventDefault();

        const hasExistingPhoto = !!(patient?.urlPictureProfile || (isDoctor && doctor?.urlProfessionalPhoto));
        const hasNewPhoto = formData.foto_perfil instanceof File;
        if (showFotoField && !hasExistingPhoto && !hasNewPhoto) {
            alertaGeneral({
                titulo: 'Foto requerida',
                messageHtml: 'Selecciona y recorte una foto de perfil antes de continuar.',
                isError: true,
            });
            return;
        }

        const hasExistingDocument = !!(patient?.urlFileDocument || (isDoctor && doctor?.urlIdDocument));
        const hasNewDocument = formData.cedulaPDF instanceof File;
        if (showDocumentoField && !hasExistingDocument && !hasNewDocument) {
            alertaGeneral({
                titulo: 'Documento requerido',
                messageHtml: 'Adjunte su documento de identidad (PDF o imagen) antes de continuar.',
                isError: true,
            });
            return;
        }

        const formDataToSend = new FormData();
        
        // Preparar datos para enviar, asegurando que los datos del doctor se incluyan si es necesario
        const dataToSend = { ...formData };
        
        // Si es doctor, asegurar que email, cellNumber, idType e idNumber se incluyan desde el perfil del doctor si están disponibles
        if (isDoctor && doctor) {
            if (!dataToSend.email && doctor.email) {
                dataToSend.email = doctor.email;
            }
            if (!dataToSend.cellNumber && doctor.cellNumber) {
                dataToSend.cellNumber = doctor.cellNumber;
            }
            if (!dataToSend.idType && doctor.idType) {
                dataToSend.idType = doctor.idType.toUpperCase();
            }
            if (!dataToSend.idNumber && doctor.idNumber) {
                dataToSend.idNumber = doctor.idNumber;
            }
        }
        
        for (const key in dataToSend) {
            // No enviar URLs de foto/documento como campos de texto; solo archivos reales
            if (PATIENT_FILE_KEYS.includes(key) && !(dataToSend[key] instanceof File)) {
                continue;
            }

            // Para doctores: no enviar foto/documento si ya los tienen en tu perfil médico
            // El backend los tomará automáticamente
            if (isDoctor && doctor) {
                if (key === 'foto_perfil' && doctor.urlProfessionalPhoto && !dataToSend[key]?.name) {
                    continue; // No enviar si el doctor ya tiene foto y no se subió nueva
                }
                if (key === 'cedulaPDF' && doctor.urlIdDocument && !dataToSend[key]?.name) {
                    continue; // No enviar si el doctor ya tiene documento y no se subió nuevo
                }
            }
            
            // Solo agregar si es un archivo real o si tiene valor
            if (dataToSend[key] !== null && dataToSend[key] !== undefined && dataToSend[key] !== '') {
                if (dataToSend[key] instanceof File) {
                    if (key === 'foto_perfil' && dataToSend[key].type?.startsWith('image/')) {
                        try {
                            const compressed = await compressProfileImage(dataToSend[key]);
                            formDataToSend.append(key, compressed);
                        } catch {
                            formDataToSend.append(key, dataToSend[key]);
                        }
                    } else {
                        formDataToSend.append(key, dataToSend[key]);
                    }
                } else {
                    formDataToSend.append(key, dataToSend[key]);
                }
            }
        }
        const clinicId = localStorage.getItem('clinicId'); // Obtén el clinicId del localStorage
        formDataToSend.append('clinicId', clinicId);

        // Usar patientId del contexto si no existe patient._id (para doctores que crean perfil por primera vez)
        let patientIdToUse = patient?._id || patientId;
        
        // Si es doctor y no hay patientId, el backend lo manejará buscando por user
        // En este caso, podemos usar un ID dummy ya que el backend buscará por usuario si no encuentra por ID
        // El backend está preparado para manejar este caso en editPatientProfile
        if (!patientIdToUse) {
            if (isDoctor) {
                // Para doctores, el backend buscará por user si no encuentra por ID
                // Usamos '000000000000000000000000' como ID temporal (no válido)
                // El backend detectará que no es válido y buscará por usuario
                patientIdToUse = '000000000000000000000000';
            } else {
                alertaGeneral({
                    titulo: 'Error',
                    messageHtml: 'No se pudo determinar el ID del paciente. Por favor, recarga la página e intenta nuevamente.',
                    isError: true
                });
                return;
            }
        }

        alertaCargandoProceso({
            titulo: 'Guardando perfil del paciente',
            messageHtml: isDoctor && doctor?.urlProfessionalPhoto && doctor?.urlIdDocument 
                ? 'Guardando perfil... La foto y documento se tomarán automáticamente de tu perfil médico.'
                : 'Por favor, espera un momento mientras se guarda el perfil del paciente',
            funcionAsync: async () => {
                try {
                    const res = await apiClient.put(`/api/patient/editProfile/${patientIdToUse}`, formDataToSend, {
                        timeout: 120000,
                    });
                    if (res.status === 201) {
                        if (draftStorageKey) {
                            localStorage.removeItem(draftStorageKey);
                            clearPatientDraftFiles(draftStorageKey);
                        }
                        alertaToastTopEnd({
                            mensaje: 'Perfil del paciente guardado correctamente',
                        });
                        const imageUrl = res.data?.image || res.data?.urlPictureProfile || '';
                        if (imageUrl) {
                            localStorage.setItem('imageProfile', imageUrl);
                            window.dispatchEvent(new CustomEvent('imageProfileUpdated', { detail: imageUrl }));
                            if (role === 'patient') {
                                localStorage.setItem('showTeleconsultasModal', 'true');
                            }
                        }
                        window.location.reload();
                    }
                } catch (error) {
                    let msg = error.response?.data?.message || 'Ocurrió un error al guardar el perfil del paciente';
                    if (error.response?.status === 413) {
                        msg = 'La foto o el documento superan el tamaño permitido. Intente con una imagen más pequeña.';
                    } else if (error.code === 'ECONNABORTED') {
                        msg = 'La subida tardó demasiado. Verifica su conexión e intente de nuevo.';
                    }
                    alertaGeneral({
                        titulo: 'Error',
                        messageHtml: msg,
                        isError: true,
                    });
                }
            }
        })
    };

    // Determinar si se deben mostrar los campos de foto y documento
    // Pacientes: siempre mostrar sección de foto en completar perfil (p. ej. registrados con Google pueden querer ver/cambiar la foto)
    // Doctores: mostrar solo si no tienen ya foto del perfil médico
    const showFotoField = isDoctor ? (!patient?.urlPictureProfile && !doctor?.urlProfessionalPhoto) : true;
    const showDocumentoField = !patient?.urlFileDocument && (!isDoctor || !doctor?.urlIdDocument);
    const hasDoctorPhoto = isDoctor && doctor?.urlProfessionalPhoto;
    const hasDoctorDocument = isDoctor && doctor?.urlIdDocument;

    return (
        <div className="w-full mx-auto p-8 bg-white">
            {/* Pantalla inicial - Primer Paso */}
            {!showForm ? (
                <div className="space-y-9">
                    <div className="max-w-2xl mx-auto text-center">
                        <div className="w-20 h-20 mx-auto mb-6 bg-[#1f94c2ff] rounded-full flex items-center justify-center">
                            <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        </div>
                        <div className="mb-6">
                            <div className="inline-block px-4 py-2 bg-blue-50 text-[#1f94c2ff] text-sm font-semibold rounded-full mb-4">
                                ⭐ Primer Paso
                            </div>
                            <h2 className="text-2xl font-bold text-[#70706eff] mb-3">
                                Bienvenido a la Plataforma
                            </h2>
                            <p className="text-base text-[#6e787aff] mb-2">
                                Para acceder a todas nuestras teleconsultas, necesitamos que completes tu perfil con tu información personal.
                            </p>
                            <p className="text-sm text-[#6e787aff] font-medium">
                                Este es el primer paso que debes realizar como nuevo usuario.
                            </p>
                        </div>
                        <div className="bg-white border-2 border-[#1f94c2ff] rounded-lg p-6 mb-6 shadow-sm">
                            <h3 className="text-xl font-semibold text-[#70706eff] mb-3">¿Estás listo?</h3>
                            <p className="text-sm text-[#6e787aff] mb-6">Completa tu perfil para comenzar a agendar tus teleconsultas</p>
                            <button 
                                className="button-add w-full max-w-md mx-auto py-3 text-base font-medium transition-all duration-200 hover:shadow-md"
                                onClick={() => setShowForm(true)}
                            >
                                Completar mi Perfil
                            </button>
                        </div>
                    </div>
                </div>
            ) : (
                <div>
                    <h2 className="text-2xl font-bold text-[#1f94c2ff] mb-6 text-center">
                        {isDoctor ? 'Perfil de Usuario (Paciente)' : 'Perfil del paciente'}
                    </h2>
            
            {/* Mensaje informativo para doctores */}
            {isDoctor && (hasDoctorPhoto || hasDoctorDocument) && (
                <div className="mb-6 p-4 bg-blue-50 border-l-4 border-blue-400 rounded-r">
                    <div className="flex items-start">
                        <div className="flex-shrink-0">
                            <svg className="h-5 w-5 text-blue-400 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                            </svg>
                        </div>
                        <div className="ml-3 flex-1">
                            <p className="text-sm text-blue-700">
                                <strong>Información importante:</strong> Como miembro registrado, 
                                {hasDoctorPhoto && hasDoctorDocument 
                                    ? ' tu foto de perfil y documento de identidad se tomarán automáticamente de tu perfil médico. No es necesario subirlos nuevamente.'
                                    : hasDoctorPhoto 
                                    ? ' tu foto de perfil se tomará automáticamente de tu perfil médico.'
                                    : ' tu documento de identidad se tomará automáticamente de tu perfil médico.'}
                            </p>
                        </div>
                    </div>
                </div>
            )}

            <form className="space-y-6" onSubmit={handleSubmit} encType="multipart/form-data">
                <div className="p-4 bg-amber-50 border-l-4 border-amber-500 rounded-r mb-2">
                    <p className="text-sm text-amber-800">
                        <strong>Importante:</strong> Esta información se completa solo una vez y no podrá editarse después. Por favor verifica que todos los datos sean correctos antes de enviar.
                    </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {showFotoField ? (
                        <div className="col-span-1">
                            <label className="block text-md font-semibold text-[#70706eff] mb-2">
                                Foto de Perfil {(isDoctor ? !hasDoctorPhoto : !patient?.urlPictureProfile) && <span className="text-red-500">*</span>}
                            </label>
                            {(patient?.urlPictureProfile || (isDoctor && hasDoctorPhoto)) && (
                                <p className="text-sm text-gray-600 mb-2">
                                    {isDoctor && hasDoctorPhoto
                                        ? 'Se usará la foto de tu perfil médico. Puedes cambiarla subiendo una nueva imagen o mantener la actual.'
                                        : 'Ya tienes una foto de perfil. Puedes cambiarla subiendo una nueva imagen o mantener la actual.'}
                                </p>
                            )}
                            <InputFile
                                fileName={formData.foto_perfil?.name || (formData.foto_perfil instanceof File ? formData.foto_perfil.name : '') || ((patient?.urlPictureProfile || (isDoctor && hasDoctorPhoto)) ? 'Mantener foto actual' : 'No se ha seleccionado ningún archivo')}
                                name="foto_perfil"
                                onChange={handleChange}
                                accept="image/jpeg,image/jpg,image/png,image/webp"
                            />
                            {(photoPreviewUrl && !photoPreviewError) ? (
                                <div className="mt-4 flex flex-col items-center gap-2">
                                    <p className="text-xs text-gray-500">Vista previa de tu foto de perfil</p>
                                    <div className="w-28 h-28 rounded-full overflow-hidden border-4 border-[#1f94c2] bg-gray-100 shadow-md">
                                        <img
                                            src={photoPreviewUrl}
                                            alt="Vista previa foto de perfil"
                                            className="w-full h-full object-cover"
                                            onError={() => setPhotoPreviewError(true)}
                                        />
                                    </div>
                                    {formData.foto_perfil instanceof File && (
                                        <p className="text-xs text-green-600 font-medium">✓ Foto lista para guardar</p>
                                    )}
                                </div>
                            ) : photoPreviewError ? (
                                <p className="mt-2 text-xs text-amber-700">No se pudo mostrar la vista previa. La foto se subirá igual al guardar.</p>
                            ) : null}
                        </div>
                    ) : hasDoctorPhoto && (
                        <div className="col-span-1">
                            <label className="block text-md font-semibold text-[#70706eff] mb-2">
                                Foto de Perfil
                            </label>
                            <div className="mt-1 p-3 bg-green-50 border border-green-200 rounded-md">
                                <p className="text-sm text-green-700">
                                    ✓ Se usará la foto de tu perfil médico
                                </p>
                            </div>
                        </div>
                    )}

                    {showDocumentoField ? (
                        <div className="col-span-1">
                            <label className="block text-md font-semibold text-[#70706eff] mb-2">
                                Documento de Identidad {(showDocumentoField && (!isDoctor || !hasDoctorDocument)) && <span className="text-red-500">*</span>}
                            </label>
                            <InputFile
                                fileName={formData.cedulaPDF?.name}
                                name="cedulaPDF"
                                onChange={handleChange}
                                accept="application/pdf,image/*"
                            />
                            {isDoctor && hasDoctorDocument && (
                                <p className="mt-2 text-xs text-gray-500">
                                    Se usará el documento de tu perfil médico automáticamente
                                </p>
                            )}
                        </div>
                    ) : hasDoctorDocument && (
                        <div className="col-span-1">
                            <label className="block text-md font-semibold text-[#70706eff] mb-2">
                                Documento de Identidad
                            </label>
                            <div className="mt-1 p-3 bg-green-50 border border-green-200 rounded-md">
                                <p className="text-sm text-green-700">
                                    ✓ Se usará el documento de tu perfil médico
                                </p>
                            </div>
                        </div>
                    )}

                    <div className="col-span-1">
                        <label className="block text-md font-semibold text-[#70706eff]">Fecha de Nacimiento *</label>
                        <input
                            type="date"
                            name="dob"
                            value={formData.dob}
                            onChange={handleChange}
                            className="mt-1 block w-full px-3 py-2 border border-[#9e9e9dff] rounded-md focus:outline-none"
                            required
                        />
                    </div>

                    {/* País de Nacimiento */}
                    <div className="form-group">
                        <label className="block text-md font-semibold text-[#70706eff]">País de Nacimiento *</label>
                        <Select
                            name="birthCountry"
                            value={opcionesPaises.find(option => option.value === formData.birthCountry)}
                            onChange={(selectedOption) =>
                                setFormData({ ...formData, birthCountry: selectedOption.value })
                            }
                            options={opcionesPaises}
                            placeholder="Selecciona un país"
                            className="w-full"
                        />
                    </div>

                    {/* País de Residencia */}
                    <div className="form-group">
                        <label className="block text-md font-semibold text-[#70706eff]">País de Residencia *</label>
                        <Select
                            name="residenceCountry"
                            value={opcionesPaises.find(option => option.value === formData.residenceCountry)}
                            onChange={(selectedOption) =>
                                setFormData({ ...formData, residenceCountry: selectedOption.value })
                            }
                            options={opcionesPaises}
                            placeholder="Selecciona un país"
                            className="w-full"
                        />
                    </div>

                    {/* Estado */}
                    <div className="form-group">
                        <label className="block text-md font-semibold text-[#70706eff]">Municipio / Ciudad *</label>
                        <Select
                            name="municipio"
                            value={opcionesMunicipios.find(option => option.value === formData.residenceCountry)}
                            onChange={(selectedOption) =>
                                setFormData({ ...formData, municipio: selectedOption.value })
                            }
                            options={opcionesMunicipios}
                            placeholder="Selecciona un municipio"
                            className="w-full"
                        />
                    </div>

                    {/* Dirección */}
                    <div className="form-group">
                        <label className="block text-md font-semibold text-[#70706eff]">Dirección</label>
                        <input
                            type="text"
                            name="address"
                            value={formData.address} //
                            onChange={handleChange}
                            className="mt-1 block w-full px-3 py-2 border border-[#9e9e9dff] rounded-md focus:outline-none"
                            placeholder="Dirección"

                        />
                    </div>


                    {/* Barrio */}
                    <div className="col-span-1">
                        <label className="block text-md font-semibold text-[#70706eff]">Barrio *</label>
                        <input
                            type="text"
                            name="neighborhood"
                            value={formData.neighborhood}
                            onChange={handleChange}
                            className="mt-1 block w-full px-3 py-2 border border-[#9e9e9dff] rounded-md focus:outline-none"
                            placeholder="Barrio"
                            required
                        />
                    </div>

                    {/* Zona Ubicación */}
                    <div className="col-span-1">
                        <label className="block text-md font-semibold text-[#70706eff]">Zona de ubicación</label>
                        <select
                            name="zonaUbicacion"
                            value={formData.zonaUbicacion}
                            onChange={handleChange}
                            className="mt-1 block w-full px-3 py-2 border border-[#9e9e9dff] rounded-md focus:outline-none"
                            required
                        >
                            <option value="">Selecciona una opción</option>
                            <option value="Urbana">Urbana</option>
                            <option value="Rural">Rural</option>
                        </select>
                    </div>


                    {/* Primer Nombre */}
                    <div className="col-span-1">
                        <label className="block text-md font-semibold text-[#70706eff]">Primer Nombre *</label>
                        <input
                            type="text"
                            name="firstName"
                            value={formData.firstName}
                            onChange={handleChange}
                            className="mt-1 block w-full px-3 py-2 border border-[#9e9e9dff] rounded-md focus:outline-none"
                            placeholder="Primer Nombre"
                            required
                        />
                    </div>

                    {/* Segundo Nombre */}
                    <div className="col-span-1">
                        <label className="block text-md font-semibold text-[#70706eff]">Segundo Nombre</label>
                        <input
                            type="text"
                            name="secondName"
                            value={formData.secondName}
                            onChange={handleChange}
                            className="mt-1 block w-full px-3 py-2 border border-[#9e9e9dff] rounded-md focus:outline-none"
                            placeholder="Segundo Nombre"
                        />
                    </div>

                    {/* Primer Apellido */}
                    <div className="col-span-1">
                        <label className="block text-md font-semibold text-[#70706eff]">Primer Apellido *</label>
                        <input
                            type="text"
                            name="firstLastName"
                            value={formData.firstLastName}
                            onChange={handleChange}
                            className="mt-1 block w-full px-3 py-2 border border-[#9e9e9dff] rounded-md focus:outline-none"
                            placeholder="Primer Apellido"
                            required
                        />
                    </div>

                    {/* Segundo Apellido */}
                    <div className="col-span-1">
                        <label className="block text-md font-semibold text-[#70706eff]">Segundo Apellido</label>
                        <input
                            type="text"
                            name="secondLastName"
                            value={formData.secondLastName}
                            onChange={handleChange}
                            className="mt-1 block w-full px-3 py-2 border border-[#9e9e9dff] rounded-md focus:outline-none"
                            placeholder="Segundo Apellido"
                        />
                    </div>

                    {/* Género */}
                    <div className="col-span-1">
                        <label className="block text-md font-semibold text-[#70706eff]">Grupo sanguíneo</label>
                        <select
                            name="grupoSanguineo"
                            value={formData.grupoSanguineo}
                            onChange={handleChange}
                            className="mt-1 block w-full px-3 py-2 border border-[#9e9e9dff] rounded-md focus:outline-none"
                            required
                        >
                            <option value="">Selecciona una opción</option>
                            <option value="A">A</option>
                            <option value="B">B</option>
                            <option value="O">O</option>
                            <option value="AB">AB</option>
                        </select>
                    </div>
                    <div className="col-span-1">
                        <label className="block text-md font-semibold text-[#70706eff]">RH</label>
                        <select
                            name="rh"
                            value={formData.rh}
                            onChange={handleChange}
                            className="mt-1 block w-full px-3 py-2 border border-[#9e9e9dff] rounded-md focus:outline-none"
                            required
                        >

                            <option value="">Selecciona una opción</option>
                            <option value="Positivo">Positivo</option>
                            <option value="Negativo">Negativo</option>
                        </select>
                    </div>
                    <div className="col-span-1">
                        <label className="block text-md font-semibold text-[#70706eff]">Género *</label>
                        <select
                            name="gender"
                            value={formData.gender}
                            onChange={handleChange}
                            className="mt-1 block w-full px-3 py-2 border border-[#9e9e9dff] rounded-md focus:outline-none"
                            required
                        >
                            <option value="" disabled>Selecciona un género</option>
                            <option value="male">Masculino</option>
                            <option value="female">Femenino</option>
                            <option value="non-binary">No Binario</option>
                            <option value="other">Otro</option>
                            <option value="prefer-not-to-say">Prefiero no decirlo</option>
                        </select>
                    </div>
                    <div className="col-span-1">
                        <label className="block text-md font-semibold text-[#70706eff]">Sexo</label>
                        <select
                            name="sex"
                            value={formData.sex}
                            onChange={handleChange}
                            className="mt-1 block w-full px-3 py-2 border border-[#9e9e9dff] rounded-md focus:outline-none"
                            required
                        >
                            <option value="">Selecciona una opción</option>
                            <option value="H">Hombre</option>
                            <option value="M">Mujer</option>
                            <option value="I">Indeterminado/Intersexual</option>
                        </select>
                    </div>
                    <div className="col-span-1">
                        <label className="block text-md font-semibold text-[#70706eff]">Estado civil</label>
                        <select
                            name="estadoCivil"
                            value={formData.estadoCivil}
                            onChange={handleChange}
                            className="mt-1 block w-full px-3 py-2 border border-[#9e9e9dff] rounded-md focus:outline-none"
                            required
                        >
                            <option value="">Selecciona una opción</option>
                            <option value="Soltero">Soltero(A)</option>
                            <option value="Casado">Casado(A)</option>
                            <option value="Viudo">Viudo(A)</option>
                            <option value="Divorciado">Divorciado(A)</option>
                            <option value="Separado">Separado(A)</option>
                            <option value="Unión Libre">Unión Libre</option>
                            <option value="No Aplica">No Aplica</option>
                        </select>
                    </div>
                    <div className="col-span-1">
                        <label className="block text-md font-semibold text-[#70706eff]">Condición de desplazamiento</label>
                        <select
                            name="condicionDesplazamiento"
                            value={formData.condicionDesplazamiento}
                            onChange={handleChange}
                            className="mt-1 block w-full px-3 py-2 border border-[#9e9e9dff] rounded-md focus:outline-none"
                            required
                        >
                            <option value="">Selecciona una opción</option>
                            <option value="Desplazados internos">Desplazados internos</option>
                            <option value="Poblacion desplazada por la violencia">Población desplazada por la violencia</option>
                            <option value="Desplazamiento forzado">Desplazamiento forzado</option>
                            <option value="No aplica">No aplica</option>
                        </select>
                    </div>
                    <div className="col-span-1">
                        <label className="block text-md font-semibold text-[#70706eff]">Grupo étnico</label>
                        <select
                            name="grupoEtnico"
                            value={formData.grupoEtnico}
                            onChange={handleChange}
                            className="mt-1 block w-full px-3 py-2 border border-[#9e9e9dff] rounded-md focus:outline-none"
                            required
                        >
                            <option value="">Selecciona un grupo étnico</option>
                            <option value="ROM (GITANOS)">ROM (GITANOS)</option>
                            <option value="AFROCOLOMBIANOS">AFROCOLOMBIANOS (Negro(a), mulato(a), afro colombiano(a) o afro descendiente)</option>
                            <option value="INDIGENAS">INDÍGENAS</option>
                            <option value="RAIZALES">RAIZALES (del archipiélago de San Andrés y Providencia)</option>
                            <option value="PALENQUERO">PALENQUERO (de San Basilio)</option>
                            <option value="Sin pertenencia étnica">Sin pertenencia étnica</option>
                        </select>
                    </div>
                    <div className="col-span-1">
                        <label className="block text-md font-semibold text-[#70706eff]">Escolaridad</label>
                        <select
                            name="escolaridad"
                            value={formData.escolaridad}
                            onChange={handleChange}
                            className="mt-1 block w-full px-3 py-2 border border-[#9e9e9dff] rounded-md focus:outline-none"
                            required
                        >
                            <option value="">Selecciona una opción</option>
                            <option value="Básica Primaria">Básica Primaria</option>
                            <option value="Básica Secundaria">Básica Secundaria</option>
                            <option value="Técnica Profesional">Técnica Profesional</option>
                            <option value="Profesional">Profesional</option>
                            <option value="Especialización">Especialización</option>
                            <option value="Ninguno">Ninguno</option>
                            <option value="Otro">Otro</option>
                            <option value="Preescolar">Preescolar</option>
                            <option value="Media Académica o Clásica">Media Académica o Clásica</option>
                            <option value="Media Técnica (Bachillerato Técnico)">Media Técnica (Bachillerato Técnico)</option>
                            <option value="Normalista">Normalista</option>
                            <option value="Tecnológica">Tecnológica</option>
                            <option value="Maestría">Maestría</option>
                            <option value="Doctorado">Doctorado</option>
                            <option value="Posgrado">Posgrado</option>
                        </select>
                    </div>

                    {/* Número de Celular */}
                    <div className="col-span-1">
                        <label className="block text-md font-semibold text-[#70706eff]">Número de Celular *</label>
                        <input
                            type="text"
                            name="cellNumber"
                            value={formData.cellNumber}
                            maxLength={10}
                            onChange={handleChange}
                            className="mt-1 block w-full px-3 py-2 border border-[#9e9e9dff] rounded-md focus:outline-none"
                            placeholder="Número de Celular"
                            required
                        />
                        {isDoctor && doctor?.cellNumber && (
                            <p className="mt-1 text-xs text-gray-500">
                                ✓ Pre-llenado desde tu perfil médico
                            </p>
                        )}
                    </div>
                    <div className="col-span-1">
                        <label className="block text-md font-semibold text-[#70706eff]">Número de Celular 2</label>
                        <input
                            type="text"
                            name="cellNumber1"
                            maxLength={10}
                            value={formData.cellNumber1}
                            onChange={handleChange}
                            className="mt-1 block w-full px-3 py-2 border border-[#9e9e9dff] rounded-md focus:outline-none"
                            placeholder="Número de Celular"

                        />
                    </div>

                    {/* Correo Electrónico */}
                    <div className="col-span-1">
                        <label className="block text-md font-semibold text-[#70706eff]">Correo Electrónico *</label>
                        <input
                            type="email"
                            name="email"
                            value={formData.email}
                            className="mt-1 block w-full px-3 py-2 border border-[#9e9e9dff] rounded-md focus:outline-none bg-gray-50"
                            readOnly
                        />
                        {isDoctor && doctor?.email && (
                            <p className="mt-1 text-xs text-gray-500">
                                ✓ Se usará el correo de tu perfil médico
                            </p>
                        )}
                    </div>

                    {/* Tipo de ID */}
                    <div className="col-span-1">
                        <label className="block text-md font-semibold text-[#70706eff]">Tipo de documento *</label>
                        {isDoctor && doctor?.idType ? (
                            <>
                                <input
                                    type="text"
                                    name="idType"
                                    value={formData.idType}
                                    className="mt-1 block w-full px-3 py-2 border border-[#9e9e9dff] rounded-md focus:outline-none bg-gray-50"
                                    readOnly
                                />
                                <p className="mt-1 text-xs text-gray-500">
                                    ✓ Se usará el tipo de documento de tu perfil médico
                                </p>
                            </>
                        ) : (
                            <select
                                name="idType"
                                value={formData.idType}
                                onChange={handleSelectChange}
                                className="mt-1 block w-full px-3 py-2 border border-[#9e9e9dff] rounded-md focus:outline-none"
                                required
                            >
                                <option value="">Selecciona</option>
                                <option value="CC">Cédula ciudadanía</option>
                                <option value="TI">Tarjeta de identidad</option>
                                <option value="CE">Cédula de extranjería</option>
                                <option value="RC">Registro civil</option>
                                <option value="PA">Pasaporte</option>
                                <option value="CD">Carné diplomático</option>
                                <option value="SC">Salvoconducto de permanencia</option>
                                <option value="PE">Permiso Especial de Permanencia</option>
                                <option value="CN">Certificado de nacido vivo</option>
                                <option value="AS">Adulto sin identificar</option>
                                <option value="MS">Menor sin identificar</option>
                                <option value="DE">Documento extranjero</option>
                                <option value="SI">Sin identificación</option>
                            </select>
                        )}
                    </div>

                    {/* Número de documento */}
                    <div className="col-span-1">
                        <label className="block text-md font-semibold text-[#70706eff]">Número de documento *</label>
                        <input
                            type="text"
                            name="idNumber"
                            value={formData.idNumber}
                            onChange={handleChange}
                            className={`mt-1 block w-full px-3 py-2 border border-[#9e9e9dff] rounded-md focus:outline-none ${isDoctor && doctor?.idNumber ? 'bg-gray-50' : ''}`}
                            placeholder="Número de documento"
                            readOnly={isDoctor && doctor?.idNumber ? true : false}
                            required
                        />
                        {isDoctor && doctor?.idNumber && (
                            <p className="mt-1 text-xs text-gray-500">
                                ✓ Se usará el número de documento de tu perfil médico
                            </p>
                        )}
                    </div>
                    <div className="col-span-1">
                        <label className="block text-md font-semibold text-[#70706eff]">Tipo de usuario</label>
                        <select
                            name="tipoUsuario"
                            value={formData.tipoUsuario}
                            onChange={handleSelectChange}
                            className="mt-1 block w-full px-3 py-2 border border-[#9e9e9dff] rounded-md focus:outline-none"
                            required
                        >
                            <option value="">Selecciona un tipo de usuario</option>
                            <option value="01">Contributivo cotizante</option>
                            <option value="02">Contributivo beneficiario</option>
                            <option value="03">Contributivo adicional</option>
                            <option value="04">Subsidiado</option>
                            <option value="05">No afiliado</option>
                            <option value="06">Especial o Excepción cotizante</option>
                            <option value="07">Especial o Excepción beneficiario</option>
                            <option value="08">Personas privadas de la libertad a cargo del Fondo Nacional de Salud</option>
                            <option value="09">Tomador / Amparado ARL</option>
                            <option value="10">Tomador / Amparado SOAT</option>
                            <option value="11">Tomador / Amparado Planes voluntarios de salud</option>
                            <option value="12">Particular</option>
                        </select>
                    </div>

                    <div className="col-span-1">
                        <label className="block text-md font-semibold text-[#70706eff]">Ocupación</label>
                        <Select
                            name="ocupacion"
                            value={selectedOcupacion}
                            onChange={handleOcupacionChange}
                            onInputChange={(inputValue) => {
                                if (inputValue.length > 2) {
                                    fetchOcupacionOptions(inputValue);
                                } else {
                                    setOcupacionOptions([]); // Limpiar las opciones si el input es corto
                                }
                            }}
                            options={[{ value: 'N/A', label: 'N/A' }, ...ocupacionOptions]}
                            isLoading={loading}
                            className="mt-1 block w-full"
                            placeholder="Buscar..."
                        />
                    </div>
                    <div className="col-span-1">
                        <label className="block text-md font-semibold text-[#70706eff]">EPS</label>
                        <Select
                            name="aseguradora"
                            value={selectedAseguradora}
                            onChange={handleAseguradoraChange}
                            onInputChange={(inputValue) => {
                                if (inputValue.length > 2) {
                                    fetchAseguradoraOptions(inputValue);
                                } else {
                                    setAseguradoraOptions([]); // Limpiar las opciones si el input es corto
                                }
                            }}
                            options={aseguradoraOptions}
                            isLoading={loading}
                            className="mt-1 block w-full"
                            placeholder="Buscar..."
                        />
                    </div>
                </div>

                {/* Cuidador o Representante */}
                <div className="col-span-1">
                    <label className="block text-md font-semibold text-[#70706eff]">¿Tiene Cuidador o Representante?</label>
                    <select
                        name="hasCarer"
                        value={formData.hasCarer}
                        onChange={handleSelectChange}
                        className="mt-1 block w-full px-3 py-2 border border-[#9e9e9dff] rounded-md focus:outline-none"
                    >
                        <option value="no">No</option>
                        <option value="yes">Sí</option>
                    </select>
                </div>

                {
                    formData.hasCarer === 'yes' && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                            {/* Primer Nombre del Cuidador */}
                            <div className="col-span-1">
                                <label className="block text-md font-semibold text-[#70706eff]">Primer Nombre del Cuidador</label>
                                <input
                                    type="text"
                                    name="carerFirstName"
                                    value={formData.carerFirstName}
                                    onChange={handleChange}
                                    className="mt-1 block w-full px-3 py-2 border border-[#9e9e9dff] rounded-md focus:outline-none"
                                    placeholder="Primer Nombre del Cuidador"
                                />
                            </div>

                            {/* Segundo Nombre del Cuidador */}
                            <div className="col-span-1">
                                <label className="block text-md font-semibold text-[#70706eff]">Segundo Nombre del Cuidador</label>
                                <input
                                    type="text"
                                    name="carerSecondName"
                                    value={formData.carerSecondName}
                                    onChange={handleChange}
                                    className="mt-1 block w-full px-3 py-2 border border-[#9e9e9dff] rounded-md focus:outline-none"
                                    placeholder="Segundo Nombre del Cuidador"
                                />
                            </div>

                            {/* Primer Apellido del Cuidador */}
                            <div className="col-span-1">
                                <label className="block text-md font-semibold text-[#70706eff]">Primer Apellido del Cuidador</label>
                                <input
                                    type="text"
                                    name="carerFirstLastName"
                                    value={formData.carerFirstLastName}
                                    onChange={handleChange}
                                    className="mt-1 block w-full px-3 py-2 border border-[#9e9e9dff] rounded-md focus:outline-none"
                                    placeholder="Primer Apellido del Cuidador"
                                />
                            </div>

                            {/* Segundo Apellido del Cuidador */}
                            <div className="col-span-1">
                                <label className="block text-md font-semibold text-[#70706eff]">Segundo Apellido del Cuidador</label>
                                <input
                                    type="text"
                                    name="carerSecondLastName"
                                    value={formData.carerSecondLastName}
                                    onChange={handleChange}
                                    className="mt-1 block w-full px-3 py-2 border border-[#9e9e9dff] rounded-md focus:outline-none"
                                    placeholder="Segundo Apellido del Cuidador"
                                />
                            </div>

                            {/* Parentesco */}
                            <div className="col-span-1">
                                <label className="block text-md font-semibold text-[#70706eff]">Parentesco</label>
                                <input
                                    type="text"
                                    name="carerRelationship"
                                    value={formData.carerRelationship}
                                    onChange={handleChange}
                                    className="mt-1 block w-full px-3 py-2 border border-[#9e9e9dff] rounded-md focus:outline-none"
                                    placeholder="Parentesco"
                                />
                            </div>

                            {/* Tipo de ID del Cuidador */}
                            <div className="col-span-1">
                                <label className="block text-md font-semibold text-[#70706eff]">Tipo de ID del Cuidador</label>
                                <select
                                    name="carerIdType"
                                    value={formData.carerIdType}
                                    onChange={handleSelectChange}
                                    className="mt-1 block w-full px-3 py-2 border border-[#9e9e9dff] rounded-md focus:outline-none"
                                >
                                    <option value="">Selecciona</option>
                                    <option value="CC">CC</option>
                                    <option value="TI">TI</option>
                                    <option value="CE">CE</option>
                                    <option value="PS">PS</option>
                                    <option value="DE">DE</option>
                                </select>
                            </div>

                            {/* Número de ID del Cuidador */}
                            <div className="col-span-1">
                                <label className="block text-md font-semibold text-[#70706eff]">Número de ID del Cuidador</label>
                                <input
                                    type="text"
                                    name="carerIdNumber"
                                    value={formData.carerIdNumber}
                                    onChange={handleChange}
                                    className="mt-1 block w-full px-3 py-2 border border-[#9e9e9dff] rounded-md focus:outline-none"
                                    placeholder="Número de ID del Cuidador"
                                />
                            </div>
                        </div>
                    )
                }

                <div className="flex justify-center mt-6">
                    <button
                        type="submit"
                        className="font-medium rounded-md px-4 py-1 border border-[#6e787aff] bg-[#f79865ff] text-[#205087ff]"
                    >
                        Guardar
                    </button>
                </div>
            </form >
                </div>
            )}

            {/* Modal de recortador: renderizado en document.body. Sin overlay propio cuando hay archivo para no tapar el ImageCropper (él ya tiene su overlay). */}
            {showImageCropper && typeof document !== 'undefined' && (() => {
                const file = imageToCrop || fileToCropRef.current;
                if (file) {
                    return createPortal(
                        <ImageCropper
                            imageFile={file}
                            onCropComplete={handleCropComplete}
                            onCropCancel={handleCropCancel}
                        />,
                        document.body
                    );
                }
                return createPortal(
                    <div
                        className="fixed inset-0 flex items-center justify-center"
                        style={{
                            position: 'fixed',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            zIndex: 2147483647,
                            backgroundColor: 'rgba(0,0,0,0.75)',
                        }}
                    >
                        <div className="bg-white rounded-xl p-6 shadow-xl text-center max-w-sm mx-4">
                            <p className="text-gray-600 mb-4">No se detectó la imagen. Por favor, elige de nuevo un archivo de imagen.</p>
                            <button
                                type="button"
                                onClick={handleCropCancel}
                                className="px-4 py-2 bg-[#1f94c2ff] text-white rounded-lg hover:bg-[#1a7a9d]"
                            >
                                Cerrar
                            </button>
                        </div>
                    </div>,
                    document.body
                );
            })()}
        </div >
    );

    function validarTipoExpresionRegular(e) {
        if (e.target.name.includes('Name')) {
            return validarInputExpresionRegular(e, /[^a-zA-ZñÑ\s]/g);
        } else if (e.target.name.includes('Number')) {
            return validarInputExpresionRegular(e, /[^0-9]/g);
        }
        return e.target.value;
    }
}

export default CompleteProfilePatient;